import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { useEventContext } from '@/src/v2/components/EventContext';
import EventPage from '../../chrome/EventPage';
import { Crumb, SaveBar, SectionCard, Select, Textarea, TextInput } from '../../components/forms';
import { PageHeadActions } from '../../components/nav';
import { useToast } from '../../components/roster';
import { Segmented, TCard, TEmpty, TSkeleton } from '../../components/table';
import { IconExternal } from './gallery/parts';
import {
    ATTIO,
    AsyncConfirm,
    BACKDROPS,
    BOOTH_TYPES,
    EVENT_TYPES,
    fmtAt,
    fullName,
    label,
    PROVISIONAL,
    reason,
    RowSelect,
    SOFTWARE,
    STATUSES,
    statusLabel,
    statusOf,
    TIMEZONES,
    useInflight,
} from './production/parts';
import { dirtySections, fromRecord, PForm, PKey, sectionPayload, validate } from './production/form';
import CrewSection from './production/CrewSection';
import GearSection from './production/GearSection';
import ContactsSection from './production/ContactsSection';
import DesignSection from './production/DesignSection';
import TimelineSection from './production/TimelineSection';
import '../../pages-event.css';
import './gallery.css';
import './production.css';

/*
 * Production — the event's ops record (v2's ActivationOpsSections).
 * Status, crew, gear, contacts and design save on their own; Details ·
 * Schedule · Venue · Leads · Booth · Comms ride one save bar. Every request
 * goes through useProduction exactly as v2's did.
 */

type Load = 'loading' | 'ready' | 'missing' | 'denied' | 'failed';

interface Lists {
    staff: any[];
    venues: any[];
    gear: any[];
    contacts: any[];
    failed: { staff: boolean; venues: boolean; gear: boolean; contacts: boolean };
}

/** Form field in the two-column grid; `wide` spans both columns. */
function F({ label: l, wide, error, help, children }: { label: string; wide?: boolean; error?: string; help?: React.ReactNode; children: React.ReactNode }) {
    return (
        <label className={wide ? 'field prd-span' : 'field'}>
            <span className="field-l">{l}</span>
            {children}
            {error ? <span className="field-e">{error}</span> : help && <span className="field-h">{help}</span>}
        </label>
    );
}

/** Select over a value list, with a blank and the current value kept if it isn't listed. */
function Pick({
    value,
    options,
    blank,
    disabled,
    onChange,
    render = label,
}: {
    value: string;
    options: Array<{ value: string; label: string }> | string[];
    blank?: string;
    disabled?: boolean;
    onChange: (v: string) => void;
    render?: (v: string) => string;
}) {
    const opts = (options as any[]).map((o) => (typeof o === 'string' ? { value: o, label: render(o) } : o)) as Array<{ value: string; label: string }>;
    return (
        <Select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
            {blank !== undefined && <option value="">{blank}</option>}
            {value && !opts.some((o) => o.value === value) && <option value={value}>{render(value)}</option>}
            {opts.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </Select>
    );
}

function ProductionBody() {
    const navigate = useNavigate();
    const { activationId: id, galleryId, resolved } = useEventContext();
    const api = useProduction();
    const toast = useToast();
    const { busy, run } = useInflight();

    const [record, setRecord] = useState<any | null>(null);
    const [load, setLoad] = useState<Load>('loading');
    const [attempt, setAttempt] = useState(0);
    const [stale, setStale] = useState(false);
    const [lists, setLists] = useState<Lists>({
        staff: [],
        venues: [],
        gear: [],
        contacts: [],
        failed: { staff: false, venues: false, gear: false, contacts: false },
    });

    const [form, setForm] = useState<PForm | null>(null);
    const [errors, setErrors] = useState<Partial<Record<PKey, string>>>({});
    const [saveError, setSaveError] = useState<string | null>(null);

    const [statusPending, setStatusPending] = useState<string | null>(null);
    const [statusConfirm, setStatusConfirm] = useState<string | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [dupOpen, setDupOpen] = useState(false);
    const [dupError, setDupError] = useState<string | null>(null);
    const [delOpen, setDelOpen] = useState(false);
    const [delError, setDelError] = useState<string | null>(null);
    const [enableError, setEnableError] = useState<string | null>(null);

    const alive = useRef(true);
    useEffect(
        () => () => {
            alive.current = false;
        },
        [],
    );

    /* same reads as v2; only a 404 (or an empty body) means "no production yet" */
    useEffect(() => {
        if (!id) return;
        let mounted = true;
        setLoad('loading');
        const soft = (p: Promise<any>) => p.then((v) => ({ ok: true, v: v ?? [] })).catch(() => ({ ok: false, v: [] as any[] }));
        Promise.all([
            api.getProductionRecord(id).then(
                (r) => ({ r, status: 200 }),
                (e) => ({ r: null, status: statusOf(e) ?? 0 }),
            ),
            soft(api.getStaff()),
            soft(api.getVenues()),
            soft(api.getGear()),
            soft(api.getContacts()),
        ]).then(([rec, staff, venues, gear, contacts]) => {
            if (!mounted) return;
            setLists({
                staff: staff.v,
                venues: venues.v,
                gear: gear.v,
                contacts: contacts.v,
                failed: { staff: !staff.ok, venues: !venues.ok, gear: !gear.ok, contacts: !contacts.ok },
            });
            if (rec.r) {
                setRecord(rec.r);
                setForm(fromRecord(rec.r));
                setErrors({});
                setLoad('ready');
            } else if (rec.status === 404 || rec.status === 200) setLoad('missing');
            else if (rec.status === 403 || rec.status === 401) setLoad('denied');
            else setLoad('failed');
        });
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, attempt]);

    /* re-read after a write; a failed re-read never hides the page (v2 flipped to "no job data") */
    const refresh = async () => {
        try {
            const r = await api.getProductionRecord(id);
            if (!alive.current) return;
            if (r) {
                setRecord(r);
                setStale(false);
            }
        } catch {
            if (alive.current) setStale(true);
        }
    };

    const base = useMemo(() => (record ? fromRecord(record) : null), [record]);

    /* when the record reloads, untouched fields follow it; edited ones stay */
    const prevBase = useRef<PForm | null>(null);
    useEffect(() => {
        const prev = prevBase.current;
        prevBase.current = base;
        if (!prev || !base) return;
        setForm((f) => {
            if (!f) return f;
            const next = { ...f } as any;
            (Object.keys(base) as PKey[]).forEach((k) => {
                if (JSON.stringify(f[k]) === JSON.stringify(prev[k])) next[k] = base[k];
            });
            return next;
        });
    }, [base]);

    const dirty = useMemo(() => (form && base ? dirtySections(base, form) : []), [form, base]);
    const saving = !!busy.save;

    const set = <K extends PKey>(k: K, v: PForm[K]) => {
        setForm((f) => (f ? { ...f, [k]: v } : f));
        setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
        setSaveError(null);
    };

    const save = async () => {
        if (!form || !base || !record || saving || dirty.length === 0) return;
        const errs = validate(base, form, dirty);
        setErrors(errs);
        if (Object.values(errs).some(Boolean)) return;
        setSaveError(null);
        const payload = dirty.reduce<Record<string, any>>((acc, s) => ({ ...acc, ...sectionPayload(s, form, { venues: lists.venues, record }) }), {});
        const r = await run('save', () => api.updateProduction(id, payload));
        if (!alive.current) return;
        if (r.ok) {
            /* reflect what was saved even if the re-read fails */
            const venue = lists.venues.find((v) => +v.id === +payload.venueId);
            const merged = { ...record, ...payload, ...(venue ? { venueAddress: venue.address } : {}) };
            prevBase.current = fromRecord(merged);
            setRecord(merged);
            setForm(fromRecord(merged));
            toast.show(`Saved ${dirty.join(' · ')}.`);
            refresh();
            /* name, date and venue also change the event header */
            window.dispatchEvent(new CustomEvent('ps-event-changed', { detail: id }));
        } else if (!('skipped' in r && r.skipped)) {
            setSaveError(reason(r.error, 'Couldn’t save — nothing was changed. Try again.'));
        }
    };

    const discard = () => {
        if (base) setForm(base);
        setErrors({});
        setSaveError(null);
    };

    /* ---- status ---- */
    const applyStatus = async (status: string) => {
        if (!record) return;
        setStatusError(null);
        setStatusPending(status);
        const r = await run('status', () => api.updateProductionStatus(id, status));
        if (r.ok) {
            await refresh();
            setStatusConfirm(null);
            toast.show(`Status set to ${statusLabel(status).toLowerCase()}.`);
        } else if (!('skipped' in r && r.skipped)) {
            setStatusError(reason(r.error, `Couldn’t change the status — it’s still ${statusLabel(record.status).toLowerCase()}.`));
        }
        setStatusPending(null);
    };
    const pickStatus = (status: string) => {
        if (!record || status === record.status) return;
        setStatusError(null);
        /* pending and lost hide the event from event lists; crossing that line is confirmed */
        if (PROVISIONAL.includes(status) !== PROVISIONAL.includes(record.status)) setStatusConfirm(status);
        else applyStatus(status);
    };

    /* ---- duplicate / delete / enable ---- */
    const duplicate = async () => {
        setDupError(null);
        const r = await run('dup', () => api.duplicateProduction(id));
        if (r.ok && r.value?.id) {
            setDupOpen(false);
            navigate(`/activations/${r.value.id}/production`);
        } else if (r.ok) {
            setDupError('The copy was made but its link didn’t come back — find it in Pipeline.');
        } else if (!('skipped' in r && r.skipped)) {
            setDupError(reason(r.error, 'Couldn’t duplicate — no copy was made. Try again.'));
        }
    };

    const remove = async () => {
        setDelError(null);
        const r = await run('delete', () => api.deleteProduction(id));
        if (r.ok) {
            setDelOpen(false);
            navigate(r.value?.deleteActivation ? '/activations' : `/activations/${id}`);
        } else if (!('skipped' in r && r.skipped)) {
            setDelError(reason(r.error, 'Couldn’t delete — nothing was removed. Try again.'));
        }
    };

    const enable = async () => {
        setEnableError(null);
        const r = await run('enable', () => api.enableOps(id));
        if (r.ok) setAttempt((a) => a + 1);
        else if (!('skipped' in r && r.skipped)) setEnableError(reason(r.error, 'Couldn’t add production details — try again.'));
    };

    const overview = <Crumb label="Overview" onClick={() => navigate(`/activations/${id}`)} />;

    /* ---- states ---- */
    if (load === 'loading') {
        return (
            <div className="ev-page prd-page">
                <PageHeadActions title="Production" sub="Loading…" />
                <TCard>
                    <TSkeleton rows={5} />
                </TCard>
            </div>
        );
    }
    if (load !== 'ready' || !record || !form || !base) {
        return (
            <div className="ev-page prd-page">
                {overview}
                <PageHeadActions title="Production" />
                <TCard>
                    {load === 'missing' ? (
                        <TEmpty
                            message="No production details on this event yet — schedule, venue, crew, gear and booth setup live here once added."
                            actionLabel={busy.enable ? 'Adding…' : 'Add production details'}
                            onAction={enable}
                        />
                    ) : load === 'denied' ? (
                        <TEmpty message="Production is managed by Smilebooth ops admins." />
                    ) : (
                        <TEmpty message="Couldn’t load production details." actionLabel="Retry" onAction={() => setAttempt((a) => a + 1)} />
                    )}
                </TCard>
                {enableError && (
                    <p className="field-e" role="alert">
                        {enableError}
                    </p>
                )}
            </div>
        );
    }

    const tz = record.timezone;
    const eventName = record.name || 'this event';
    const status = statusPending ?? record.status;
    const staffOpts = lists.staff
        .map((s) => ({ value: String(s.id), label: fullName(s) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const leadName = (k: 'producer' | 'technicalLead' | 'logisticsCoordinator') =>
        record[`${k}FirstName`] ? fullName({ firstName: record[`${k}FirstName`], lastName: record[`${k}LastName`] }) : 'Current lead';
    const venueOpts = lists.venues.map((v) => ({ value: String(v.id), label: v.name }));
    const sameVenue = form.venueId !== '' && +form.venueId === +record.venueId;
    const picked = lists.venues.find((v) => +v.id === +form.venueId);
    const venueAddress = picked?.address ?? (sameVenue ? record.venueAddress : null);
    const tzOpts = TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES];
    const unsaved = dirty.length > 0 ? ' Unsaved changes on this page aren’t included.' : '';
    const sub = [statusLabel(record.status), fmtAt(record.startAt, tz)].filter(Boolean).join(' · ');

    return (
        <div className="ev-page prd-page">
            {overview}
            <PageHeadActions
                title={record.name || 'Production'}
                sub={sub}
                actions={
                    <button
                        type="button"
                        className="tb-btn"
                        disabled={!!busy.dup}
                        onClick={() => {
                            setDupError(null);
                            setDupOpen(true);
                        }}
                    >
                        Duplicate
                    </button>
                }
            />

            {stale && (
                <p className="prd-stale" role="status">
                    <span>Couldn’t refresh after the last change — what’s shown may be out of date.</span>
                    <button type="button" className="tb-btn sm" onClick={() => refresh()}>
                        Refresh
                    </button>
                </p>
            )}

            <div className="stack">
                {/* ---- saves on change ---- */}
                <SectionCard title="Status">
                    <div className="prd-status">
                        <RowSelect
                            value={status}
                            options={STATUSES}
                            render={statusLabel}
                            disabled={!!busy.status}
                            ariaLabel="Production status"
                            onChange={pickStatus}
                        />
                        <span className="field-h prd-status-h">
                            {PROVISIONAL.includes(record.status)
                                ? 'Hidden from event lists while pending or lost.'
                                : 'Saves as soon as you pick. Pending and lost hide the event from event lists.'}
                        </span>
                    </div>
                    {statusError && !statusConfirm && (
                        <p className="field-e" role="alert">
                            {statusError}
                        </p>
                    )}
                    {(record.jobId || record.attioDealId) && (
                        <div className="prd-links">
                            {record.jobId && (
                                <div className="frow">
                                    <span className="fl">Job</span>
                                    <span className="fv">
                                        <a
                                            className="link"
                                            href={`/pipeline/${record.jobId}`}
                                            onClick={(e) => {
                                                if (e.metaKey || e.ctrlKey) return;
                                                e.preventDefault();
                                                navigate(`/pipeline/${record.jobId}`);
                                            }}
                                        >
                                            {record.jobName ?? `Job ${record.jobId}`}
                                        </a>
                                    </span>
                                </div>
                            )}
                            {record.attioDealId && (
                                <div className="frow">
                                    <span className="fl">Attio deal</span>
                                    <span className="fv">
                                        <a className="link prd-ext" href={`${ATTIO}/deals/record/${record.attioDealId}/overview`} target="_blank" rel="noreferrer">
                                            Open in Attio
                                            <IconExternal />
                                        </a>
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </SectionCard>

                {/* ---- batched: one save bar ---- */}
                <SectionCard title="Details">
                    <div className="prd-grid">
                        <F label="Name" wide error={errors.name} help="Also renames the event everywhere.">
                            <TextInput value={form.name} disabled={saving} aria-invalid={errors.name ? true : undefined} onChange={(e) => set('name', e.target.value)} />
                        </F>
                        <F label="Event name — as the client calls it" wide>
                            <TextInput value={form.eventName} disabled={saving} onChange={(e) => set('eventName', e.target.value)} />
                        </F>
                        <F label="Type">
                            <Pick value={form.eventType} options={EVENT_TYPES} disabled={saving} onChange={(v) => set('eventType', v)} />
                        </F>
                        <F label="Billing entity">
                            <TextInput value={form.billingEntity} disabled={saving} onChange={(e) => set('billingEntity', e.target.value)} />
                        </F>
                        <F label="Guests" error={errors.guestCount}>
                            <TextInput
                                type="number"
                                inputMode="numeric"
                                min={0}
                                value={form.guestCount}
                                disabled={saving}
                                aria-invalid={errors.guestCount ? true : undefined}
                                onChange={(e) => set('guestCount', e.target.value)}
                            />
                        </F>
                        <F label="Description" wide>
                            <Textarea value={form.description} disabled={saving} onChange={(e) => set('description', e.target.value)} />
                        </F>
                    </div>
                </SectionCard>

                <SectionCard title="Schedule" note={form.timezone}>
                    <div className="prd-grid">
                        <F label="Delivery">
                            <TextInput type="datetime-local" value={form.deliveryAt} disabled={saving} onChange={(e) => set('deliveryAt', e.target.value)} />
                        </F>
                        <F label="Show ready">
                            <TextInput type="datetime-local" value={form.showReadyAt} disabled={saving} onChange={(e) => set('showReadyAt', e.target.value)} />
                        </F>
                        <F label="Start" help="Also sets the event date.">
                            <TextInput type="datetime-local" value={form.startAt} disabled={saving} onChange={(e) => set('startAt', e.target.value)} />
                        </F>
                        <F label="End" error={errors.endAt}>
                            <TextInput
                                type="datetime-local"
                                value={form.endAt}
                                disabled={saving}
                                aria-invalid={errors.endAt ? true : undefined}
                                onChange={(e) => set('endAt', e.target.value)}
                            />
                        </F>
                        <F
                            label="Timezone"
                            wide
                            help={form.timezone !== base.timezone ? 'The times above stay as typed and are read in the new timezone.' : 'Times are entered in this timezone.'}
                        >
                            <Pick value={form.timezone} options={tzOpts.map((z) => ({ value: z, label: z }))} render={(z) => z} disabled={saving} onChange={(v) => set('timezone', v)} />
                        </F>
                    </div>
                </SectionCard>

                <SectionCard title="Venue">
                    <div className="prd-grid">
                        <F
                            label="Venue"
                            wide
                            error={lists.failed.venues ? 'Couldn’t load venues — the venue can’t be changed right now.' : undefined}
                            help={venueAddress || (form.venueId === '' ? 'Picking a venue fills empty load-in, parking and internet fields from its defaults.' : undefined)}
                        >
                            <Pick
                                value={form.venueId}
                                options={venueOpts}
                                blank="No venue"
                                render={() => record.venueName ?? 'Current venue'}
                                disabled={saving || lists.failed.venues}
                                onChange={(v) => {
                                    const venue = lists.venues.find((x) => +x.id === +v);
                                    setForm((d) =>
                                        d
                                            ? {
                                                  ...d,
                                                  venueId: v,
                                                  /* venue defaults prefill empty fields (v2) */
                                                  loadInInstructions: d.loadInInstructions || venue?.defaultLoadInInstructions || '',
                                                  parkingInstructions: d.parkingInstructions || venue?.defaultParkingInstructions || '',
                                                  internetProvided: d.internetProvided || !!venue?.defaultInternetProvided,
                                                  internetDetails: d.internetDetails || venue?.defaultInternetDetails || '',
                                              }
                                            : d,
                                    );
                                    setSaveError(null);
                                }}
                            />
                        </F>
                        <F label="Setup location" wide>
                            <TextInput
                                placeholder="e.g. Ballroom B, north wall"
                                value={form.setupLocation}
                                disabled={saving}
                                onChange={(e) => set('setupLocation', e.target.value)}
                            />
                        </F>
                        <div className="field">
                            <span className="field-l">Internet</span>
                            <Segmented<'yes' | 'no'>
                                label="Internet"
                                value={form.internetProvided ? 'yes' : 'no'}
                                onChange={(v) => !saving && set('internetProvided', v === 'yes')}
                                options={[
                                    { value: 'no', label: 'Not provided' },
                                    { value: 'yes', label: 'Provided' },
                                ]}
                            />
                        </div>
                        <F label="Internet details">
                            <TextInput placeholder="Network, password…" value={form.internetDetails} disabled={saving} onChange={(e) => set('internetDetails', e.target.value)} />
                        </F>
                        <F label="Load-in" wide>
                            <Textarea value={form.loadInInstructions} disabled={saving} onChange={(e) => set('loadInInstructions', e.target.value)} />
                        </F>
                        <F label="Parking" wide>
                            <Textarea value={form.parkingInstructions} disabled={saving} onChange={(e) => set('parkingInstructions', e.target.value)} />
                        </F>
                    </div>
                </SectionCard>

                <SectionCard title="Leads" note="no one is emailed">
                    {lists.failed.staff && <p className="field-e">Couldn’t load the staff roster — leads can’t be changed right now.</p>}
                    <div className="prd-grid prd-grid3">
                        <F label="Producer">
                            <Pick
                                value={form.producerId}
                                options={staffOpts}
                                blank="Unassigned"
                                render={() => leadName('producer')}
                                disabled={saving || lists.failed.staff}
                                onChange={(v) => set('producerId', v)}
                            />
                        </F>
                        <F label="Technical lead">
                            <Pick
                                value={form.technicalLeadId}
                                options={staffOpts}
                                blank="Unassigned"
                                render={() => leadName('technicalLead')}
                                disabled={saving || lists.failed.staff}
                                onChange={(v) => set('technicalLeadId', v)}
                            />
                        </F>
                        <F label="Logistics">
                            <Pick
                                value={form.logisticsCoordinatorId}
                                options={staffOpts}
                                blank="Unassigned"
                                render={() => leadName('logisticsCoordinator')}
                                disabled={saving || lists.failed.staff}
                                onChange={(v) => set('logisticsCoordinatorId', v)}
                            />
                        </F>
                    </div>
                </SectionCard>

                <SectionCard title="Booth">
                    <div className="prd-grid prd-grid3">
                        <F label="Booth type">
                            <Pick value={form.boothType} options={BOOTH_TYPES} blank="—" disabled={saving} onChange={(v) => set('boothType', v)} />
                        </F>
                        <F label="Software">
                            <Pick value={form.software} options={SOFTWARE} blank="—" disabled={saving} onChange={(v) => set('software', v)} />
                        </F>
                        <F label="Backdrop">
                            <Pick value={form.backdrop} options={BACKDROPS} blank="—" disabled={saving} onChange={(v) => set('backdrop', v)} />
                        </F>
                        <F label="Printing">
                            <TextInput value={form.printing} disabled={saving} onChange={(e) => set('printing', e.target.value)} />
                        </F>
                        <F label="Slideshow">
                            <TextInput value={form.slideshow} disabled={saving} onChange={(e) => set('slideshow', e.target.value)} />
                        </F>
                        <F label="Attire">
                            <TextInput value={form.attire} disabled={saving} onChange={(e) => set('attire', e.target.value)} />
                        </F>
                        <F label="Equipment notes" wide>
                            <Textarea value={form.equipment} disabled={saving} onChange={(e) => set('equipment', e.target.value)} />
                        </F>
                    </div>
                </SectionCard>

                <SectionCard title="Comms & links">
                    <div className="prd-grid">
                        <F label="Email subject" wide>
                            <TextInput value={form.emailSubject} disabled={saving} onChange={(e) => set('emailSubject', e.target.value)} />
                        </F>
                        <F label="Email body" wide>
                            <Textarea value={form.emailBody} disabled={saving} onChange={(e) => set('emailBody', e.target.value)} />
                        </F>
                        <F label="Text message" wide>
                            <Textarea value={form.textMessageBody} disabled={saving} onChange={(e) => set('textMessageBody', e.target.value)} />
                        </F>
                        <F
                            label="Dropbox folder"
                            wide
                            error={errors.dropboxFolderUrl}
                            help={
                                base.dropboxFolderUrl && form.dropboxFolderUrl === base.dropboxFolderUrl ? (
                                    <a className="link prd-ext" href={base.dropboxFolderUrl} target="_blank" rel="noreferrer">
                                        Open folder
                                        <IconExternal />
                                    </a>
                                ) : undefined
                            }
                        >
                            <TextInput
                                type="url"
                                className="input ev-mono"
                                placeholder="https://"
                                value={form.dropboxFolderUrl}
                                disabled={saving}
                                aria-invalid={errors.dropboxFolderUrl ? true : undefined}
                                onChange={(e) => set('dropboxFolderUrl', e.target.value)}
                            />
                        </F>
                        <F label="Notes" wide>
                            <Textarea value={form.notes} disabled={saving} onChange={(e) => set('notes', e.target.value)} />
                        </F>
                    </div>
                </SectionCard>

                {saveError && (
                    <p className="field-e" role="alert">
                        {saveError}
                    </p>
                )}
                {Object.values(errors).some(Boolean) && (
                    <p className="field-e" role="alert">
                        Fix the highlighted fields to save.
                    </p>
                )}
            </div>

            <SaveBar dirty={dirty} saving={saving} onSave={save} onDiscard={discard} />

            {/* ---- separately saved ---- */}
            <div className="stack gl-own">
                <CrewSection
                    api={api}
                    activationId={id}
                    eventName={eventName}
                    crew={record.staffAssignments ?? []}
                    staff={lists.staff}
                    staffFailed={lists.failed.staff}
                    refresh={refresh}
                    toast={toast.show}
                />
                <GearSection
                    api={api}
                    activationId={id}
                    gear={record.gearAssignments ?? []}
                    assets={lists.gear}
                    assetsFailed={lists.failed.gear}
                    refresh={refresh}
                    toast={toast.show}
                />
                <ContactsSection
                    api={api}
                    activationId={id}
                    contacts={record.contacts ?? []}
                    directory={lists.contacts}
                    directoryFailed={lists.failed.contacts}
                    refresh={refresh}
                    toast={toast.show}
                />
                <DesignSection api={api} activationId={id} requests={record.designRequests ?? []} staff={lists.staff} refresh={refresh} toast={toast.show} />
                <TimelineSection activity={record.activity ?? []} staff={lists.staff} />

                <SectionCard title="Delete production">
                    <div className="prd-danger">
                        <p className="field-h prd-danger-t">
                            Removes this event’s schedule, venue, booth, crew, gear, contacts and design requests. Events without galleries are deleted
                            entirely.
                        </p>
                        <button
                            type="button"
                            className="tb-btn gl-danger"
                            disabled={!!busy.delete}
                            onClick={() => {
                                setDelError(null);
                                setDelOpen(true);
                            }}
                        >
                            Delete production…
                        </button>
                    </div>
                </SectionCard>
            </div>

            <AsyncConfirm
                open={!!statusConfirm}
                title={`Mark ${eventName} ${statusLabel(statusConfirm).toLowerCase()}?`}
                actionLabel={`Mark ${statusLabel(statusConfirm).toLowerCase()}`}
                busyLabel="Saving…"
                busy={!!busy.status}
                error={statusError}
                onConfirm={() => statusConfirm && applyStatus(statusConfirm)}
                onClose={() => {
                    setStatusConfirm(null);
                    setStatusError(null);
                }}
            >
                <p>
                    {statusConfirm && PROVISIONAL.includes(statusConfirm)
                        ? `It moves from ${statusLabel(record.status).toLowerCase()} to ${statusLabel(statusConfirm).toLowerCase()}, and the event is hidden from event lists until it moves back. Nothing is deleted and no one is emailed.`
                        : `It moves from ${statusLabel(record.status).toLowerCase()} to ${statusLabel(statusConfirm).toLowerCase()}, and the event shows in event lists again. No one is emailed.`}
                </p>
            </AsyncConfirm>

            <AsyncConfirm
                open={dupOpen}
                title={`Duplicate ${eventName}?`}
                actionLabel="Duplicate"
                busyLabel="Duplicating…"
                busy={!!busy.dup}
                error={dupError}
                onConfirm={duplicate}
                onClose={() => setDupOpen(false)}
            >
                <p>
                    Creates “{record.name} (Copy)” in the same job as a pending event, hidden from event lists, with these production details and contacts.
                    Crew, gear and design requests aren’t copied, and no one is emailed.{unsaved}
                </p>
            </AsyncConfirm>

            <AsyncConfirm
                open={delOpen}
                title={`Delete production for ${eventName}?`}
                actionLabel="Delete production"
                busyLabel="Deleting…"
                busy={!!busy.delete}
                error={delError}
                danger
                onConfirm={remove}
                onClose={() => setDelOpen(false)}
            >
                <p>
                    This permanently removes the schedule, venue, booth setup, {(record.staffAssignments ?? []).length} crew,{' '}
                    {(record.gearAssignments ?? []).length} gear reservations, {(record.contacts ?? []).length} contacts and{' '}
                    {(record.designRequests ?? []).length} design requests for <b>{eventName}</b>. It can’t be undone. Crew aren’t emailed.
                </p>
                <p>
                    {resolved && galleryId == null
                        ? 'This event has no gallery, so the event itself is deleted too.'
                        : 'The event and its galleries stay; only the production details go.'}
                </p>
            </AsyncConfirm>

            {toast.node}
        </div>
    );
}

export default function ProductionV3() {
    return (
        <EventPage section="production">
            <ProductionBody />
        </EventPage>
    );
}
